import { useState } from "react";
import { Modal } from "@/components/Modal";

/** "Type the label to confirm" gate for destructive ops (GitHub-style).
 *
 *     <ConfirmType
 *       open={askingDelete}
 *       title="Delete real key?"
 *       explanation="This is permanent. Every bound proxy key must already be revoked."
 *       confirmPhrase={real.label}
 *       dangerLabel="Delete"
 *       onCancel={() => setAskingDelete(false)}
 *       onConfirm={doDelete}
 *     />
 */
export function ConfirmType({
  open,
  title,
  explanation,
  confirmPhrase,
  dangerLabel,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  explanation: string;
  confirmPhrase: string;
  dangerLabel: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = typed === confirmPhrase;

  return (
    <Modal open={open} title={title} onClose={onCancel}>
      <div className="space-y-4 text-sm">
        <p className="text-slate-300">{explanation}</p>
        <div>
          <div className="text-xs text-slate-500 mb-1">
            Type <code className="mono text-slate-300">{confirmPhrase}</code> to confirm:
          </div>
          <input
            className="input mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmPhrase}
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-danger"
            disabled={!matches || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
                setTyped("");
              }
            }}
          >
            {busy ? "Working…" : dangerLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
